<template>
  <div class="resources-home-view">
    <div class="page-header">
      <div class="header-main">
        <div class="header-left">
          <h1 class="page-title">资源管理</h1>
          <p class="page-description">管理您的求职相关资源</p>
          <el-button type="info" size="small" class="simple-mode-btn" @click="goToSimpleVersion">
            切换极简版
          </el-button>
        </div>
        <div class="view-toggle">
          <el-radio-group v-model="currentView" size="large" class="custom-radio-group">
            <el-radio-button label="list">
              <el-icon><Grid /></el-icon>
              列表视图
            </el-radio-button>
            <el-radio-button label="dashboard">
              <el-icon><DataBoard /></el-icon>
              仪表盘
            </el-radio-button>
          </el-radio-group>
        </div>
      </div>
      <div v-if="currentView === 'list'" class="header-actions">
        <div class="search-box">
          <el-input
            v-model="searchText"
            placeholder="搜索公司..."
            clearable
            class="rounded-search"
            @clear="handleSearch"
          >
            <template #prefix>
              <el-icon class="search-icon"><Search /></el-icon>
            </template>
          </el-input>
          <el-button 
            circle 
            plain 
            class="search-action-button" 
            @click="handleSearch"
            title="搜索"
          >
            <el-icon><Search /></el-icon>
          </el-button>
        </div>
        <div class="resource-actions">
          <el-button type="primary" @click="openAddDialog">
            <el-icon><Plus /></el-icon> 添加公司
          </el-button>
        </div>
      </div>
    </div>

    <!-- 仪表盘视图 -->
    <div v-if="currentView === 'dashboard'" class="dashboard-view">
      <div class="dashboard-container">
        <div class="dashboard-header">
          <h2 class="dashboard-title">数据概览</h2>
          <el-button
            :loading="refreshing"
            circle
            @click="handleRefreshDashboard"
            class="refresh-button"
          >
            <el-icon><Refresh /></el-icon>
          </el-button>
        </div>
        <div class="dashboard-grid">
          <!-- 统计卡片 -->
          <el-row :gutter="20" class="stat-cards">
            <el-col :span="6">
              <el-card class="stat-card">
                <template #header>
                  <div class="stat-header">
                    <el-icon><Suitcase /></el-icon>
                    <span>已投递公司</span>
                  </div>
                </template>
                <div class="stat-content">
                  <div class="stat-number">{{ dashboardStats.submittedCompanies }}</div>
                  <div class="stat-trend">
                    较上周 <span :class="{'trend-up': dashboardStats.companyTrend > 0}">
                      {{ dashboardStats.companyTrend > 0 ? '+' : '' }}{{ dashboardStats.companyTrend }}%
                    </span>
                  </div>
                </div>
              </el-card>
            </el-col>
            <el-col :span="6">
              <el-card class="stat-card">
                <template #header>
                  <div class="stat-header">
                    <el-icon><Calendar /></el-icon>
                    <span>面试数量</span>
                  </div>
                </template>
                <div class="stat-content">
                  <div class="stat-number">{{ dashboardStats.totalInterviews }}</div>
                  <div class="stat-trend">
                    较上周 <span :class="{'trend-up': dashboardStats.interviewTrend > 0}">
                      {{ dashboardStats.interviewTrend > 0 ? '+' : '' }}{{ dashboardStats.interviewTrend }}%
                    </span>
                  </div>
                </div>
              </el-card>
            </el-col>
            <el-col :span="6">
              <el-card class="stat-card">
                <template #header>
                  <div class="stat-header">
                    <el-icon><Document /></el-icon>
                    <span>投递职位</span>
                  </div>
                </template>
                <div class="stat-content">
                  <div class="stat-number">{{ dashboardStats.totalPositions }}</div>
                  <div class="stat-trend">
                    较上周 <span :class="{'trend-up': dashboardStats.positionTrend > 0}">
                      {{ dashboardStats.positionTrend > 0 ? '+' : '' }}{{ dashboardStats.positionTrend }}%
                    </span>
                  </div>
                </div>
              </el-card>
            </el-col>
            <el-col :span="6">
              <el-card class="stat-card">
                <template #header>
                  <div class="stat-header">
                    <el-icon><Timer /></el-icon>
                    <span>求职天数</span>
                  </div>
                </template>
                <div class="stat-content">
                  <div class="stat-number">{{ dashboardStats.totalDays }}</div>
                  <div class="stat-subtitle">开始于 {{ dashboardStats.startDate }}</div>
                </div>
              </el-card>
            </el-col>
          </el-row>

          <!-- 图表区域 -->
          <el-row :gutter="20" class="chart-row">
            <el-col :span="16">
              <el-card class="chart-card">
                <template #header>
                  <div class="chart-header">
                    <span>投递趋势</span>
                    <el-radio-group v-model="trendTimeRange" size="small">
                      <el-radio-button label="week">周</el-radio-button>
                      <el-radio-button label="month">月</el-radio-button>
                      <el-radio-button label="year">年</el-radio-button>
                    </el-radio-group>
                  </div>
                </template>
                <div class="chart-content">
                  <!-- 这里将使用 ECharts 来展示投递趋势图 -->
                  <div ref="trendChartRef" style="height: 300px;"></div>
                </div>
              </el-card>
            </el-col>
            <el-col :span="8">
              <el-card class="chart-card">
                <template #header>
                  <div class="chart-header">
                    <span>公司状态分布</span>
                  </div>
                </template>
                <div class="chart-content">
                  <!-- 这里将使用 ECharts 来展示状态分布饼图 -->
                  <div ref="statusChartRef" style="height: 300px;"></div>
                </div>
              </el-card>
            </el-col>
          </el-row>

          <!-- 时间线 -->
          <el-row>
            <el-col :span="24">
              <el-card class="timeline-card">
                <template #header>
                  <div class="timeline-header">
                    <span>求职事件时间线</span>
                  </div>
                </template>
                <el-timeline>
                  <el-timeline-item
                    v-for="event in dashboardStats.recentEvents"
                    :key="event.id"
                    :timestamp="event.time"
                    :type="event.type"
                  >
                    {{ event.content }}
                  </el-timeline-item>
                </el-timeline>
              </el-card>
            </el-col>
          </el-row>
        </div>
      </div>
    </div>

    <!-- 原有的列表视图 -->
    <div v-else class="company-list-container">
      <div v-if="companyStore.loading" class="loading-container">
        <el-skeleton :rows="3" animated>
          <template #template>
            <div class="company-card-skeleton">
              <el-skeleton-item variant="h3" style="width: 50%" />
              <el-skeleton-item variant="text" style="width: 30%" />
            </div>
          </template>
        </el-skeleton>
      </div>
      
      <div v-else-if="!companyStore.companies.length" class="empty-container">
        <el-empty description="暂无公司信息">
          <el-button type="primary" @click="openAddDialog">
            添加公司
          </el-button>
        </el-empty>
      </div>
      
      <div v-else class="company-list">
        <div
          v-for="company in companyStore.companies"
          :key="company.id"
          class="company-item"
        >
          <div class="company-main" @click="handleCompanyClick(company)">
            <div class="company-info">
              <div class="company-logo">
                <el-avatar :size="48" :src="company.logo">
                  {{ company.name?.charAt(0) }}
                </el-avatar>
              </div>
              <div class="company-details">
                <div class="company-name">{{ company.name }}</div>
                <div class="company-meta">
                  <span class="company-location" v-if="company.location">
                    <el-icon><Location /></el-icon>
                    {{ company.location }}
                  </span>
                  <span class="company-industry" v-if="company.industry">
                    <el-icon><Office /></el-icon>
                    {{ company.industry }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="company-actions">
            <div class="status-tags">
              <company-status-tag :status="company.status" />
              <el-badge
                v-if="companyInterviewStatus.get(company.id as number)?.status"
                :value="companyInterviewStatus.get(company.id as number)?.status"
                :type="companyInterviewStatus.get(company.id as number)?.stage ? 
                  STAGE_TYPES[companyInterviewStatus.get(company.id as number)?.stage as keyof typeof STAGE_TYPES] : 
                  'info'"
                class="interview-status-badge"
              />
            </div>
            <div class="interaction-icons">
              <div class="icon-item" @click="handlePositionClick(company)">
                <el-icon class="custom-icon position-icon"><Suitcase /></el-icon>
                <span class="icon-label">职位</span>
              </div>
              <div class="icon-item" @click="handleResourceClick(company)">
                <el-icon class="custom-icon resource-icon"><Files /></el-icon>
                <span class="icon-label">资源</span>
              </div>
              <div class="icon-item" @click="handleInterviewClick(company)">
                <el-icon class="custom-icon interview-icon"><Calendar /></el-icon>
                <span class="icon-label">面试</span>
              </div>
            </div>
          </div>
        </div>

        <div class="pagination-container">
          <el-pagination
            v-model:current-page="companyStore.page"
            v-model:page-size="companyStore.size"
            :total="companyStore.total"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </div>
    </div>

    <!-- 添加公司对话框 -->
    <el-dialog
      v-model="addDialogVisible"
      title="添加公司"
      width="600px"
      :close-on-click-modal="false"
      :destroy-on-close="true"
    >
      <CompanyForm
        ref="addFormRef"
        :loading="companyStore.loading"
        @submit="handleAddCompany"
      />

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="addDialogVisible = false">取消</el-button>
          <el-button 
            type="primary" 
            @click="submitAddForm"
            :loading="companyStore.loading"
          >
            保存
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, reactive, watch, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { 
  Plus, 
  Star, 
  Top, 
  Search, 
  Location, 
  OfficeBuilding,
  ArrowRight,
  Office,
  Suitcase,
  Files,
  Calendar,
  Document,
  Timer,
  Grid,
  DataBoard,
  Refresh
} from '@element-plus/icons-vue';
import { useCompanyStore } from '@/store/companyStore';
import type { Company } from '@/types/company';
import CompanyForm from '@/components/company/CompanyForm.vue';
import { ElMessage } from 'element-plus';
import { usePositionStore } from '@/store/positionStore';
import interviewService from '@/services/interviewService';
import type { Interview } from '@/types/interview';
import { INTERVIEW_STAGES } from '@/types/interview';
import type { Position } from '@/types/position';
import * as echarts from 'echarts';

const router = useRouter();
const companyStore = useCompanyStore();
const positionStore = usePositionStore();
const searchText = ref('');
const addDialogVisible = ref(false);
const addFormRef = ref();

// 存储公司的面试状态
const companyInterviewStatus = ref<Map<number, { 
  status: string; 
  position: string;
  stage: string | null;
}>>(new Map());

// 修改颜色映射对象
const STAGE_TYPES = {
  'not_started': 'info',       // 未开始 - 灰色
  'online_test': 'warning',    // 在线测试 - 橙色
  'first_round': 'primary',    // 第一轮 - 蓝色
  'second_round': 'success',   // 第二轮 - 绿色
  'final_round': 'danger',     // 终面 - 红色
  'hr_round': 'warning',       // HR面 - 橙色
  'completed': 'success',      // 已完成 - 绿色
  'applying': 'info'           // 投递中 - 灰色
};

const STAGE_LABELS = {
  'not_started': '待面试',
  'online_test': '在线测试',
  'first_round': '第一轮',
  'second_round': '第二轮',
  'final_round': '终面',
  'hr_round': 'HR面',
  'completed': '已完成',
  'applying': '投递中'
};

// 获取公司的面试状态
const getCompanyInterviewStatus = async (company: Company) => {
  if (!company.id) return;
  
  try {
    // 获取公司的所有职位
    await positionStore.getPositionsByCompany(company.id, 1);
    const positions = positionStore.positions;
    
    if (positions.length === 0) {
      return;
    }

    let latestInterview: Interview | null = null;
    let latestPosition: Position | null = null;
    
    // 遍历所有职位获取面试记录
    for (const position of positions) {
      if (position.id) {
        try {
          const response = await interviewService.getInterviewsByPosition(position.id);
          let interviews: Interview[] = [];
          
          if (response && response.content && Array.isArray(response.content)) {
            interviews = response.content;
          } else if (Array.isArray(response)) {
            interviews = response;
          }
          
          if (interviews.length > 0) {
            // 按时间排序，找出最近的面试
            const sortedInterviews = [...interviews].sort((a, b) => {
              const now = new Date().getTime();
              const diffA = Math.abs(new Date(a.dateTime).getTime() - now);
              const diffB = Math.abs(new Date(b.dateTime).getTime() - now);
              return diffA - diffB;
            });
            
            // 如果这是最近的面试，更新状态
            if (!latestInterview || 
                Math.abs(new Date(sortedInterviews[0].dateTime).getTime() - new Date().getTime()) <
                Math.abs(new Date(latestInterview.dateTime).getTime() - new Date().getTime())) {
              latestInterview = sortedInterviews[0];
              latestPosition = position;
            }
          }
        } catch (error) {
          console.error(`获取职位 ${position.id} 的面试记录失败:`, error);
        }
      }
    }
    
    // 更新公司的面试状态
    if (latestInterview && latestPosition) {
      const stageLabel = STAGE_LABELS[latestInterview.stage as keyof typeof STAGE_LABELS] || '未知状态';
      companyInterviewStatus.value.set(company.id, {
        status: `${stageLabel}(${latestPosition.title})`,
        position: latestPosition.title || '',
        stage: latestInterview.stage
      });
    } else {
      companyInterviewStatus.value.set(company.id, {
        status: STAGE_LABELS.applying,
        position: '',
        stage: 'applying'
      });
    }
  } catch (error) {
    console.error('获取公司面试状态失败:', error);
  }
};

// 页面加载时获取公司列表
onMounted(async () => {
  await companyStore.queryCompanies({});
  // 获取每个公司的面试状态
  for (const company of companyStore.companies) {
    await getCompanyInterviewStatus(company);
  }
});

// 打开添加对话框
const openAddDialog = () => {
  addDialogVisible.value = true;
};

// 提交添加表单
const submitAddForm = () => {
  if (addFormRef.value) {
    addFormRef.value.submitForm();
  }
};

// 处理添加公司
const handleAddCompany = async (company: Company) => {
  try {
    await companyStore.createCompany(company);
    addDialogVisible.value = false;
    
    // 重新加载列表
    if (searchText.value.trim()) {
      await companyStore.searchCompanies({ keyword: searchText.value.trim() });
    } else {
      await companyStore.queryCompanies({});
    }
  } catch (error) {
    console.error('添加公司失败:', error);
  }
};

// 处理行点击
const handleRowClick = (company: Company) => {
  if (company.id) {
    router.push(`/resources/companies/${company.id}`);
  }
};

// 处理公司点击
const handleCompanyClick = (company: Company) => {
  if (company.id) {
    router.push(`/resources/companies/${company.id}`);
  }
};

// 处理搜索
const handleSearch = async () => {
  if (searchText.value.trim()) {
    await companyStore.searchCompanies({ 
      keyword: searchText.value.trim()
    });
  } else {
    await companyStore.queryCompanies({});
  }
  
  // 重新获取面试状态
  companyInterviewStatus.value.clear();
  for (const company of companyStore.companies) {
    await getCompanyInterviewStatus(company);
  }
};

// 处理分页大小变化
const handleSizeChange = async (size: number) => {
  companyStore.size = size;
  if (searchText.value.trim()) {
    await companyStore.searchCompanies({ 
      keyword: searchText.value.trim()
    });
  } else {
    await companyStore.queryCompanies({});
  }
};

// 处理页码变化
const handlePageChange = async (page: number) => {
  companyStore.page = page;
  if (searchText.value.trim()) {
    await companyStore.searchCompanies({ 
      keyword: searchText.value.trim() 
    });
  } else {
    await companyStore.queryCompanies({});
  }
};

const handlePositionClick = (company: Company) => {
  router.push(`/resources/companies/${company.id}#positions`);
};

const handleResourceClick = (company: Company) => {
  router.push(`/resources/companies/${company.id}#resources`);
};

const handleInterviewClick = (company: Company) => {
  router.push(`/resources/companies/${company.id}#interviews`);
};

// 视图切换状态
const currentView = ref('list');
const trendTimeRange = ref('week');
const trendChartRef = ref<HTMLElement | null>(null);
const statusChartRef = ref<HTMLElement | null>(null);

// 仪表盘数据
const dashboardStats = reactive({
  submittedCompanies: 0,
  companyTrend: 0,
  totalInterviews: 0,
  interviewTrend: 0,
  totalPositions: 0,
  positionTrend: 0,
  totalDays: 0,
  startDate: '',
  recentEvents: [] as Array<{
    id: number;
    time: string;
    content: string;
    type: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  }>
});

// 添加刷新状态
const refreshing = ref(false);

// 处理仪表盘刷新
const handleRefreshDashboard = async () => {
  refreshing.value = true;
  try {
    await fetchDashboardData();
    ElMessage.success('数据已更新');
  } catch (error) {
    console.error('刷新仪表盘失败:', error);
    ElMessage.error('刷新失败，请重试');
  } finally {
    refreshing.value = false;
  }
};

// 获取仪表盘数据
const fetchDashboardData = async () => {
  try {
    // 初始化图表实例
    const trendChart = trendChartRef.value ? echarts.init(trendChartRef.value) : null;
    const statusChart = statusChartRef.value ? echarts.init(statusChartRef.value) : null;

    // 获取所有公司数据
    const response = await companyStore.queryCompanies({});
    console.log('Dashboard query response:', response);
    console.log('Company store state after query:', {
      companies: companyStore.companies,
      total: companyStore.total,
      page: companyStore.page,
      size: companyStore.size
    });
    
    if (!Array.isArray(companyStore.companies)) {
      console.error('Companies data is not an array:', companyStore.companies);
      ElMessage.error('获取公司数据格式错误');
      return;
    }

    // 统计已投递的公司数量（排除计划投递状态）
    const submittedCompanies = companyStore.companies.length;  // 先显示总数
    console.log('Total companies count:', submittedCompanies);

    // 按状态统计
    const statusCounts = {
      planned: companyStore.companies.filter(c => c.status === 'planned').length,
      submitted: companyStore.companies.filter(c => c.status === 'submitted').length,
      interviewing: companyStore.companies.filter(c => c.status === 'interviewing').length,
      offered: companyStore.companies.filter(c => c.status === 'offered').length,
      rejected: companyStore.companies.filter(c => c.status === 'rejected').length,
      accepted: companyStore.companies.filter(c => c.status === 'accepted').length
    };
    console.log('Status counts:', statusCounts);

    // 更新仪表盘数据
    dashboardStats.submittedCompanies = submittedCompanies;
    dashboardStats.companyTrend = 0; // 暂时设为0

    // 获取面试数量
    let totalInterviews = 0;
    for (const company of companyStore.companies) {
      if (company.id) {
        try {
          // 获取公司的所有职位
          await positionStore.getPositionsByCompany(company.id, 1);
          const positions = positionStore.positions;
          
          // 遍历所有职位获取面试记录
          for (const position of positions) {
            if (position.id) {
              try {
                const response = await interviewService.getInterviewsByPosition(position.id);
                let interviews: Interview[] = [];
                
                if (response && response.content && Array.isArray(response.content)) {
                  interviews = response.content;
                } else if (Array.isArray(response)) {
                  interviews = response;
                }
                
                totalInterviews += interviews.length;
              } catch (error) {
                console.error(`获取职位 ${position.id} 的面试记录失败:`, error);
              }
            }
          }
        } catch (error) {
          console.error(`获取公司 ${company.id} 的职位失败:`, error);
        }
      }
    }

    console.log('Total interviews:', totalInterviews);
    dashboardStats.totalInterviews = totalInterviews;
    dashboardStats.interviewTrend = 0; // 暂时设为0
    dashboardStats.totalPositions = companyStore.companies.reduce((sum, company) => sum + (company.positionCount || 0), 0);
    dashboardStats.positionTrend = 0; // 暂时设为0

    // 获取第一条投递记录的时间作为开始时间
    const sortedCompanies = [...companyStore.companies].sort((a, b) => {
      const dateA = new Date(a.createTime || '');
      const dateB = new Date(b.createTime || '');
      return dateA.getTime() - dateB.getTime();
    });

    console.log('Sorted companies:', sortedCompanies.map(c => ({
      name: c.name,
      createTime: c.createTime,
      status: c.status
    })));

    if (sortedCompanies.length > 0 && sortedCompanies[0].createTime) {
      const startDate = new Date(sortedCompanies[0].createTime);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - startDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      dashboardStats.totalDays = diffDays;
      dashboardStats.startDate = startDate.toLocaleDateString('zh-CN');
      console.log('Dashboard stats updated:', dashboardStats);
    } else {
      dashboardStats.totalDays = 0;
      dashboardStats.startDate = '-';
      console.log('No valid company dates found');
    }

    // 更新状态分布图表
    if (statusChart) {
      const statusData = [
        { 
          value: statusCounts.planned,
          name: '计划投递'
        },
        { 
          value: statusCounts.submitted,
          name: '已投递'
        },
        { 
          value: statusCounts.interviewing,
          name: '面试中'
        },
        { 
          value: statusCounts.offered,
          name: '已收到offer'
        },
        { 
          value: statusCounts.rejected,
          name: '已拒绝'
        },
        { 
          value: statusCounts.accepted,
          name: '已接受'
        }
      ].filter(item => item.value > 0);

      console.log('Status chart data:', statusData);

      // 如果没有数据，显示空状态
      if (statusData.length === 0) {
        statusData.push({
          value: 1,
          name: '暂无数据'
        });
      }

      statusChart.setOption({
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          padding: 5,
          itemGap: 10,
          textStyle: {
            fontSize: 12
          }
        },
        series: [{
          type: 'pie',
          radius: '50%',
          data: statusData,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          label: {
            show: true,
            formatter: '{b}: {c}'
          }
        }]
      });
    }

    // 更新投递趋势图表
    if (trendChart) {
      const today = new Date();
      const dates = [];
      const counts = [];
      const trendData = new Map(); // 用于存储每天的投递数量

      // 根据选择的时间范围生成日期
      let days = 7;
      if (trendTimeRange.value === 'month') {
        days = 30;
      } else if (trendTimeRange.value === 'year') {
        days = 365;
      }

      // 初始化日期范围内的所有日期
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toLocaleDateString('zh-CN');
        dates.push(dateStr);
        trendData.set(dateStr, 0);
      }

      // 统计每天的投递数量
      companyStore.companies.forEach(company => {
        if (company.createTime) {
          const createDate = new Date(company.createTime);
          const dateStr = createDate.toLocaleDateString('zh-CN');
          if (trendData.has(dateStr)) {
            trendData.set(dateStr, trendData.get(dateStr)! + 1);
          }
        }
      });

      // 生成最终的计数数组
      counts.push(...dates.map(date => trendData.get(date) || 0));

      console.log('Trend chart data:', { dates, counts });

      // 计算上周期的数据用于计算趋势
      const currentPeriodTotal = counts.reduce((sum, count) => sum + count, 0);
      const previousPeriodCounts = [];
      for (let i = days * 2 - 1; i >= days; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toLocaleDateString('zh-CN');
        const count = companyStore.companies.filter(company => {
          if (!company.createTime) return false;
          const companyDate = new Date(company.createTime);
          return companyDate.toLocaleDateString('zh-CN') === dateStr;
        }).length;
        previousPeriodCounts.push(count);
      }
      const previousPeriodTotal = previousPeriodCounts.reduce((sum, count) => sum + count, 0);

      // 计算趋势百分比
      if (previousPeriodTotal > 0) {
        const trend = ((currentPeriodTotal - previousPeriodTotal) / previousPeriodTotal) * 100;
        dashboardStats.companyTrend = Math.round(trend);
      }

      trendChart.setOption({
        tooltip: {
          trigger: 'axis',
          formatter: '{b}<br />投递数量: {c}'
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLabel: {
            rotate: 45,
            interval: Math.floor(days / 7)
          }
        },
        yAxis: {
          type: 'value',
          minInterval: 1
        },
        series: [{
          data: counts,
          type: 'line',
          smooth: true,
          name: '投递数量',
          itemStyle: {
            color: '#409EFF'
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(64,158,255,0.3)' },
              { offset: 1, color: 'rgba(64,158,255,0.1)' }
            ])
          }
        }]
      });
    }

    // 生成事件时间线
    const events: Array<{
      id: number;
      time: string;
      content: string;
      type: 'primary' | 'success' | 'warning' | 'danger' | 'info';
    }> = [];

    // 收集所有事件
    const allEvents = [];

    // 添加公司创建事件
    companyStore.companies.forEach(company => {
      if (company.createTime) {
        allEvents.push({
          time: new Date(company.createTime),
          content: `添加了公司 ${company.name}`,
          type: 'primary' as const
        });
      }
    });

    // 添加面试事件
    for (const company of companyStore.companies) {
      if (company.id) {
        const positions = positionStore.positions;
        for (const position of positions) {
          if (position.id) {
            try {
              const response = await interviewService.getInterviewsByPosition(position.id);
              let interviews: Interview[] = [];
              
              if (response && response.content && Array.isArray(response.content)) {
                interviews = response.content;
              } else if (Array.isArray(response)) {
                interviews = response;
              }
              
              interviews.forEach(interview => {
                allEvents.push({
                  time: new Date(interview.dateTime),
                  content: `${company.name} - ${position.title} ${STAGE_LABELS[interview.stage as keyof typeof STAGE_LABELS]}面试`,
                  type: 'warning' as const
                });
              });
            } catch (error) {
              console.error(`获取面试记录失败:`, error);
            }
          }
        }
      }
    }

    // 排序所有事件（按时间倒序）
    allEvents.sort((a, b) => b.time.getTime() - a.time.getTime());

    // 取最近的10个事件
    dashboardStats.recentEvents = allEvents.slice(0, 10).map((event, index) => ({
      id: index + 1,
      time: event.time.toLocaleString('zh-CN'),
      content: event.content,
      type: event.type
    }));

    console.log('Recent events:', dashboardStats.recentEvents);

  } catch (error) {
    console.error('获取仪表盘数据失败:', error);
    ElMessage.error('获取数据失败，请稍后重试');
  }
};

onMounted(() => {
  fetchDashboardData();
});

// 监听时间范围变化
watch(trendTimeRange, () => {
  fetchDashboardData();
});

// 在组件卸载时清理图表实例
onUnmounted(() => {
  if (trendChartRef.value) {
    echarts.dispose(trendChartRef.value);
  }
  if (statusChartRef.value) {
    echarts.dispose(statusChartRef.value);
  }
});

// 添加跳转到极简版的方法
const goToSimpleVersion = () => {
  router.push('/resources/simple');
};
</script>

<style scoped lang="scss">
.resources-home-view {
  padding: 32px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 60px);
  
  /* 添加公司对话框样式 */
  :deep(.el-dialog) {
    border-radius: 16px;
    overflow: hidden;
    
    .el-dialog__header {
      margin: 0;
      padding: 20px;
      border-bottom: 1px solid #f0f0f0;
      
      .el-dialog__title {
        font-weight: 600;
        font-size: 18px;
      }
    }
    
    .el-dialog__body {
      padding: 20px;
    }
    
    .el-dialog__footer {
      padding: 10px 20px 20px;
      border-top: 1px solid #f0f0f0;
    }
  }
}

.page-header {
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 85%;
  margin: 0 auto 32px;

  .header-main {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .header-left {
    text-align: left;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
  }
}

.page-title {
  margin: 0;
  font-size: 2rem;
  font-weight: 600;
  color: #303133;
}

.page-description {
  margin-top: 8px;
  color: #606266;
  font-size: 1rem;
}

.view-toggle {
  margin-right: 20px;

  .custom-radio-group {
    :deep(.el-radio-button__inner) {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 10px 20px;
      border-radius: 8px;
      transition: all 0.3s ease;
      border: 1px solid #dcdfe6;
      background-color: white;
      color: #606266;
      height: auto;
      line-height: 1;

      .el-icon {
        margin-right: 4px;
        font-size: 16px;
      }

      &:hover {
        color: var(--el-color-primary);
        border-color: var(--el-color-primary-light-7);
        background-color: var(--el-color-primary-light-9);
      }
    }

    :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
      color: white;
      background-color: var(--el-color-primary);
      border-color: var(--el-color-primary);
      box-shadow: -1px 0 0 0 var(--el-color-primary);

      &:hover {
        background-color: var(--el-color-primary-dark-2);
      }
    }

    :deep(.el-radio-button:first-child .el-radio-button__inner) {
      border-radius: 8px 0 0 8px;
    }

    :deep(.el-radio-button:last-child .el-radio-button__inner) {
      border-radius: 0 8px 8px 0;
    }
  }
}

.search-box {
  flex: 1;
  min-width: 300px;
  position: relative;
  display: flex;
  align-items: center;
}

.rounded-search {
  width: 100%;
}

.rounded-search :deep(.el-input__wrapper) {
  border-radius: 20px;
  box-shadow: 0 0 0 1px #dcdfe6;
  padding-left: 8px;
  transition: all 0.3s;
}

.rounded-search :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px var(--el-color-primary);
}

.rounded-search :deep(.el-input__inner) {
  height: 40px;
  font-size: 14px;
}

.search-icon {
  font-size: 16px;
  color: #909399;
  margin-right: 4px;
}

/* 调整清除按钮的位置和样式 */
.rounded-search :deep(.el-input__clear) {
  right: 35px;
}

.search-action-button {
  position: absolute;
  right: 8px;
  height: 28px;
  width: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  border-color: transparent;
  background-color: transparent;
  z-index: 2;
}

.search-action-button:hover,
.search-action-button:focus {
  color: var(--el-color-primary);
  background-color: transparent;
  border-color: transparent;
}

.resource-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.add-button {
  white-space: nowrap;
  border-radius: 20px;
  padding: 9px 20px;
}

.company-list-container {
  width: 85%;
  margin: 0 auto;
}

.loading-container {
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.company-card-skeleton {
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.empty-container {
  padding: 60px 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.company-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.company-item {
  background: white;
  border-radius: 12px;
  padding: 24px;
  transition: all 0.3s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.company-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.1);
}

.company-main {
  flex: 1;
  cursor: pointer;
  padding-right: 24px;
}

.company-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.company-logo {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
}

.company-details {
  flex: 1;
}

.company-name {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.company-meta {
  display: flex;
  gap: 16px;
  color: #909399;
  font-size: 0.9rem;
}

.company-location {
  display: flex;
  align-items: center;
  gap: 4px;
}

.company-industry {
  display: flex;
  align-items: center;
  gap: 4px;
}

.company-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
}

.status-tags {
  display: flex;
  gap: 12px;
  align-items: center;
  order: -1;
}

.interview-status-badge {
  --el-badge-font-size: 12px;
  --el-badge-padding: 6px 10px;
  --el-badge-size: 18px;
  --el-badge-font-weight: 500;
}

.interview-status-badge :deep(.el-badge__content) {
  border: none;
  height: auto;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  line-height: 1.4;
  font-weight: 500;
}

.interview-status-badge :deep(.el-badge__content.el-badge__content--primary) {
  background-color: var(--el-color-primary-light-8);
  color: var(--el-color-primary);
}

.interview-status-badge :deep(.el-badge__content.el-badge__content--success) {
  background-color: var(--el-color-success-light-8);
  color: var(--el-color-success);
}

.interview-status-badge :deep(.el-badge__content.el-badge__content--warning) {
  background-color: var(--el-color-warning-light-8);
  color: var(--el-color-warning);
}

.interview-status-badge :deep(.el-badge__content.el-badge__content--danger) {
  background-color: var(--el-color-danger-light-8);
  color: var(--el-color-danger);
}

.interview-status-badge :deep(.el-badge__content.el-badge__content--info) {
  background-color: var(--el-color-info-light-8);
  color: var(--el-color-info);
}

.interaction-icons {
  display: flex;
  gap: 24px;
  padding: 8px 0;
}

.icon-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: transform 0.2s;
  min-width: 60px;
}

.icon-item:hover {
  transform: translateY(-2px);
}

.custom-icon {
  font-size: 24px;
  padding: 12px;
  border-radius: 12px;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.position-icon {
  color: #67c23a;
  background-color: #f0f9eb;
}

.position-icon:hover {
  background-color: #e1f3d8;
}

.resource-icon {
  color: #409eff;
  background-color: #ecf5ff;
}

.resource-icon:hover {
  background-color: #dbedff;
}

.interview-icon {
  color: #e6a23c;
  background-color: #fdf6ec;
}

.interview-icon:hover {
  background-color: #faecd8;
}

.icon-label {
  font-size: 13px;
  margin-top: 8px;
  color: #606266;
}

.pagination-container {
  margin-top: 24px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: flex-end;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.dashboard-view {
  padding: 20px;

  .dashboard-container {
    width: 85%;
    margin: 0 auto;
    max-width: 1800px;
  }

  .dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }

  .dashboard-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #303133;
  }

  .refresh-button {
    transition: all 0.3s ease;
    
    &:hover {
      transform: rotate(180deg);
    }
  }

  .dashboard-grid {
    .stat-cards {
      margin-bottom: 20px;
    }

    .stat-card {
      .stat-header {
        display: flex;
        align-items: center;
        gap: 8px;
        color: #606266;
        
        .el-icon {
          font-size: 18px;
        }
      }

      .stat-content {
        text-align: center;
        padding: 10px 0;

        .stat-number {
          font-size: 28px;
          font-weight: bold;
          color: #303133;
          margin-bottom: 8px;
        }

        .stat-trend {
          font-size: 14px;
          color: #909399;

          .trend-up {
            color: #67C23A;
          }
        }

        .stat-subtitle {
          font-size: 12px;
          color: #909399;
        }
      }
    }

    .chart-row {
      margin-bottom: 20px;
    }

    .chart-card {
      .chart-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .chart-content {
        padding: 10px;
      }
    }

    .timeline-card {
      .timeline-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .el-timeline {
        padding: 20px;
      }
    }
  }
}

@media (max-width: 1400px) {
  .resources-home-view {
    .dashboard-view {
      .dashboard-container {
        width: 95%;
      }
    }
  }
}

@media (max-width: 768px) {
  .resources-home-view {
    padding: 0 !important;
    background-color: #f5f5f7 !important;
    
    /* 移动端对话框样式 */
    :deep(.el-dialog) {
      width: 92% !important;
      max-width: 420px !important;
      margin: 0 auto !important;
      border-radius: 12px !important;
      
      .el-dialog__header {
        padding: 15px !important;
      }
      
      .el-dialog__body {
        padding: 15px !important;
        max-height: 70vh !important;
        overflow-y: auto !important;
      }
      
      .el-dialog__footer {
        padding: 10px 15px 15px !important;
      }
    }
  }
  
  .page-header {
    padding: 15px !important;
    margin: 10px auto !important;
    width: calc(100% - 20px) !important;
    border-radius: 12px !important;
    background-color: #fff !important;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
    
    .header-main {
      flex-direction: column !important;
      align-items: flex-start !important;
      gap: 10px !important;
      padding: 0 !important;
    }

    .header-actions {
      flex-direction: column !important;
      width: 100% !important;
      gap: 10px !important;
      padding: 0 !important;
    }

    .view-toggle {
      width: 100% !important;
      margin-right: 0 !important;

      .custom-radio-group {
        width: 100% !important;
        display: flex !important;

        :deep(.el-radio-button) {
          flex: 1 !important;

          .el-radio-button__inner {
            width: 100% !important;
            justify-content: center !important;
          }
        }
      }
    }

    .search-box {
      width: 100% !important;
    }

    .resource-actions {
      width: 100% !important;
      
      .el-button {
        width: 100% !important;
      }
    }
  }

  .dashboard-view {
    padding: 0 !important;
    
    .dashboard-container {
      width: 100% !important;
      margin: 0 !important;
    }
    
    .dashboard-header {
      padding: 15px !important;
      margin: 10px auto !important;
      width: calc(100% - 20px) !important;
      border-radius: 12px !important;
      background-color: #fff !important;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
    }
    
    .dashboard-grid {
      .stat-cards {
        margin: 0 10px !important;
        
        .el-col {
          width: 100% !important;
          padding: 0 !important;
          margin-bottom: 10px !important;
        }
        
        .stat-card {
          border-radius: 12px !important;
          margin-bottom: 0 !important;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
          overflow: hidden !important;
          
          .el-card__header {
            padding: 12px 15px !important;
          }
          
          .el-card__body {
            padding: 15px !important;
          }
        }
      }
      
      .chart-row {
        margin: 0 10px !important;
        
        .el-col {
          width: 100% !important;
          padding: 0 !important;
          margin-bottom: 10px !important;
        }
        
        .chart-card {
          border-radius: 12px !important;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
          overflow: hidden !important;
          
          .el-card__header {
            padding: 12px 15px !important;
          }
          
          .el-card__body {
            padding: 15px !important;
          }
          
          .chart-content {
            padding: 0 !important;
            
            div[ref="trendChartRef"],
            div[ref="statusChartRef"] {
              height: 250px !important;
              width: 100% !important;
            }
          }
        }
      }
      
      .timeline-card {
        margin: 0 10px 10px 10px !important;
        border-radius: 12px !important;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
        overflow: hidden !important;
        
        .el-card__header {
          padding: 12px 15px !important;
        }
        
        .el-card__body {
          padding: 0 !important;
        }
        
        .el-timeline {
          padding: 15px !important;
        }
      }
    }
  }
  
  .company-list-container {
    width: 100% !important;
    
    .empty-container {
      padding: 20px !important;
      margin: 10px !important;
      background-color: #fff !important;
      border-radius: 12px !important;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
    }
    
    .company-list {
      padding: 0 10px !important;
      
      .company-item {
        margin-bottom: 10px !important;
        border-radius: 12px !important;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
        overflow: hidden !important;
        padding: 15px !important;
        flex-direction: column !important;
        align-items: flex-start !important;
        
        .company-main {
          width: 100% !important;
          padding-right: 0 !important;
          margin-bottom: 10px !important;
        }
        
        .company-actions {
          width: 100% !important;
          flex-direction: column !important;
          gap: 10px !important;
          
          .status-tags {
            width: 100% !important;
            justify-content: space-between !important;
          }
          
          .interaction-icons {
            width: 100% !important;
            justify-content: space-between !important;
            padding: 10px 0 0 0 !important;
            border-top: 1px solid #f0f0f0 !important;
          }
        }
      }
      
      .pagination-container {
        margin: 10px 0 !important;
        padding: 15px !important;
        border-radius: 12px !important;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05) !important;
      }
    }
  }
}
</style> 